/**
 * El semáforo de ruido.
 *
 * Una regla pura, sin micrófono y sin navegador, para poder probarla: entra un
 * nivel entre cero y uno y sale un color. Lo que hace que sea una regla y no un
 * `if` suelto son las dos cosas que la hacen usable por un niño.
 *
 * La primera es el **suavizado**. Un portazo o una tos no son «estar gritando»,
 * y un semáforo que parpadea con cada sílaba no se puede leer. La media móvil
 * sube deprisa y baja despacio: se entera enseguida de que ha empezado el jaleo
 * y no perdona el silencio de medio segundo entre dos gritos.
 *
 * La segunda es la **histéresis**. Sin ella, un nivel justo en el límite hace
 * que el color cambie diez veces por segundo, que es exactamente lo contrario de
 * una señal. Para encenderse hay que cruzar la línea; para apagarse hay que
 * bajar bastante por debajo. Un niño que ve rojo sabe que tiene que bajar de
 * verdad, no rozar el límite desde abajo.
 */

export type NoiseLight = 'verde' | 'ambar' | 'rojo'

/** Dónde empieza el ámbar, como fracción del límite que puso el adulto. */
const AMBAR = 0.75

/** Cuánto hay que bajar para volver atrás. Es la histéresis. */
const VUELVE_DE_ROJO = 0.9
const VUELVE_DE_AMBAR = 0.6

/**
 * Sube deprisa y baja despacio. Los dos números son distintos a propósito: el
 * ruido que importa es el que dura, y el silencio que importa es el que dura
 * también, pero sólo uno de los dos conviene creérselo rápido.
 */
const SUBE = 0.5
const BAJA = 0.08

export function smooth(previous: number, sample: number): number {
  const alpha = sample > previous ? SUBE : BAJA
  return previous + alpha * (sample - previous)
}

export function noiseLight(level: number, limit: number, previous: NoiseLight): NoiseLight {
  // Un límite de cero no significa «todo es ruido»: significa que nadie lo ha
  // puesto todavía, y sin línea no hay semáforo.
  if (limit <= 0) return 'verde'

  const ratio = level / limit

  if (previous === 'rojo') {
    if (ratio >= VUELVE_DE_ROJO) return 'rojo'
    return ratio >= AMBAR ? 'ambar' : 'verde'
  }

  if (previous === 'ambar') {
    if (ratio >= 1) return 'rojo'
    return ratio >= VUELVE_DE_AMBAR ? 'ambar' : 'verde'
  }

  if (ratio >= 1) return 'rojo'
  return ratio >= AMBAR ? 'ambar' : 'verde'
}
